import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  doc, getDoc, collection, query, orderBy, limit, onSnapshot,
  addDoc, updateDoc, serverTimestamp, arrayRemove,
  where, getDocs
} from 'firebase/firestore';
import { revokeProfileDisclosure } from '../utils/profileDisclosure';
import { applicationService } from '../services/applicationService';
import { createProfileDisclosure } from '../utils/profileDisclosure';
import { showToast } from '../utils/toast';
import './Chat.css';

function GroupChat() {
  const { recruitmentId } = useParams();
  const { currentUser } = useAuth();
  const chatId = `group_${recruitmentId}`;

  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [participantDetails, setParticipantDetails] = useState({});
  const [disclosedTo, setDisclosedTo] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!recruitmentId || !currentUser) return;

    const chatRef = doc(db, 'chats', chatId);
    getDoc(chatRef).then(async snap => {
      if (!snap.exists()) {
        setLoading(false);
        return;
      }
      const data = snap.data();
      if (!data.participants.includes(currentUser.uid)) {
        showToast({ message: 'このグループチャットへのアクセス権限がありません。', type: 'error' });
        setLoading(false);
        return;
      }
      setChat({ id: snap.id, ...data });
      setIsOrganizer(data.organizerId === currentUser.uid);

      const details = {};
      await Promise.all(data.participants.map(async uid => {
        try {
          const userDoc = await getDoc(doc(db, 'users', uid));
          if (userDoc.exists()) {
            const d = userDoc.data();
            details[uid] = { nickname: d.nickname || d.displayName || '名無しの登山者' };
          }
        } catch (_) {}
      }));
      setParticipantDetails(details);

      // 自分が開示済みの相手を Firestore からロード（ownerId == 自分 のみ）
      try {
        const disclosuresSnap = await getDocs(query(
          collection(db, 'profileDisclosures'),
          where('ownerId', '==', currentUser.uid),
          where('chatId', '==', chatId),
          where('active', '==', true)
        ));
        const loaded = {};
        disclosuresSnap.forEach(d => { loaded[d.data().viewerId] = true; });
        setDisclosedTo(loaded);
      } catch (_) {}

      setLoading(false);
    });

    const q = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(200)
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsub();
  }, [recruitmentId, currentUser, chatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    if (chat?.status === 'dissolved') return;
    setSending(true);
    try {
      const myProfileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const myNickname = myProfileDoc.exists()
        ? (myProfileDoc.data().nickname || currentUser.displayName || '名無しの登山者')
        : (currentUser.displayName || '名無しの登山者');

      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        senderId: currentUser.uid,
        senderName: myNickname,
        text: newMessage.trim(),
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: newMessage.trim(),
        lastMessageAt: serverTimestamp()
      });
      setNewMessage('');
    } catch (err) {
      console.error('送信に失敗しました:', err);
    } finally {
      setSending(false);
    }
  };

  const handleRemoveParticipant = async (uid) => {
    if (!window.confirm('この参加者をグループチャットから削除し、応募承認を取り消しますか？')) return;
    try {
      const nickname = participantDetails[uid]?.nickname || '参加者';
      const chatRef = doc(db, 'chats', chatId);

      const appsQ = query(
        collection(db, 'applications'),
        where('recruitmentId', '==', recruitmentId),
        where('userId', '==', uid)
      );
      const appsSnap = await getDocs(appsQ);

      if (!appsSnap.empty) {
        await applicationService.reject(appsSnap.docs[0].id);
      }

      const currentChatSnap = await getDoc(chatRef);
      const currentNames = currentChatSnap.data().participantNames || {};
      delete currentNames[uid];
      await updateDoc(chatRef, {
        participants: arrayRemove(uid),
        participantNames: currentNames,
        updatedAt: serverTimestamp()
      });

      setChat(prev => ({
        ...prev,
        participants: prev.participants.filter(p => p !== uid)
      }));
      setParticipantDetails(prev => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
      showToast({ message: `${nickname}さんを削除しました。`, type: 'success' });
    } catch (err) {
      console.error('削除に失敗しました:', err);
      showToast({ message: '削除に失敗しました。', type: 'error' });
    }
  };

  const handleSharePrivateInfo = async (uid) => {
    const nickname = participantDetails[uid]?.nickname || chat?.participantNames?.[uid] || '参加者';
    if (!window.confirm(`${nickname}さんに非公開情報を公開しますか？`)) return;
    try {
      const recruitmentSnap = await getDoc(doc(db, 'recruitments', recruitmentId));
      await createProfileDisclosure({
        ownerId: currentUser.uid,
        viewerId: uid,
        chatId,
        recruitmentId,
        recruitment: recruitmentSnap.exists() ? recruitmentSnap.data() : null,
        createdByAction: 'group_chat_share_button',
      });
      setDisclosedTo(prev => ({ ...prev, [uid]: true }));
      showToast({ message: '非公開情報を公開しました。', type: 'success' });
    } catch (err) {
      console.error('非公開情報の公開に失敗しました:', err);
      showToast({ message: '非公開情報の公開に失敗しました。', type: 'error' });
    }
  };

  const handleRevokePrivateInfo = async (uid) => {
    const nickname = participantDetails[uid]?.nickname || chat?.participantNames?.[uid] || '参加者';
    if (!window.confirm(`${nickname}さんへの非公開情報の公開を中止しますか？`)) return;
    try {
      await revokeProfileDisclosure({
        ownerId: currentUser.uid,
        viewerId: uid,
        chatId,
      });
      setDisclosedTo(prev => ({ ...prev, [uid]: false }));
      showToast({ message: '非公開情報の公開を中止しました。', type: 'success' });
    } catch (err) {
      console.error('非公開情報の公開中止に失敗しました:', err);
      showToast({ message: '公開中止に失敗しました。', type: 'error' });
    }
  };

  const handleDissolveGroupChat = async () => {
    if (!window.confirm('グループチャットを解散しますか？\n解散すると、このグループチャットで公開した非公開情報の公開も終了します。')) return;
    try {
      await updateDoc(doc(db, 'chats', chatId), {
        status: 'dissolved',
        dissolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      const disclosuresSnap = await getDocs(query(
        collection(db, 'profileDisclosures'),
        where('chatId', '==', chatId),
        where('ownerId', '==', currentUser.uid)
      ));
      await Promise.all(disclosuresSnap.docs.map((disclosureDoc) => (
        updateDoc(disclosureDoc.ref, {
          active: false,
          revokedReason: 'group_chat_dissolved',
          updatedAt: serverTimestamp(),
          lastValidatedAt: serverTimestamp()
        })
      )));
      setChat(prev => ({ ...prev, status: 'dissolved' }));
      showToast({ message: 'グループチャットを解散しました。', type: 'success' });
    } catch (err) {
      console.error('グループチャットの解散に失敗しました:', err);
      showToast({ message: 'グループチャットの解散に失敗しました。', type: 'error' });
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;

  if (!chat) {
    return (
      <div className="chat-container">
        <div className="chat-header card">
          <Link to={`/recruitments/${recruitmentId}`} className="back-link">← 募集に戻る</Link>
        </div>
        <div className="chat-messages card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <p>グループチャットがまだ開始されていません。</p>
          <p>主催者が「グループチャット開始」ボタンを押すと開始されます。</p>
        </div>
      </div>
    );
  }

  const recruitmentName = chat.recruitmentName || '募集';
  const isDissolved = chat.status === 'dissolved';

  return (
    <div className="chat-container">
      <div className="chat-header card">
        <Link to={`/recruitments/${recruitmentId}`} className="back-link">← 募集に戻る</Link>
        <div>
          <h2>
            <span className="group-chat-badge">グループ</span>
            {recruitmentName}
          </h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
            参加者 {chat.participants.length}人{isDissolved ? ' / 解散済み' : ''}
          </p>
        </div>
        {isOrganizer && !isDissolved && (
          <button className="btn btn-sm btn-danger" onClick={handleDissolveGroupChat}>
            グループチャット解散
          </button>
        )}
      </div>

      {/* 参加者一覧 */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: '8px' }}>
        <p style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px' }}>参加メンバー</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {chat.participants.map(uid => {
            const name = participantDetails[uid]?.nickname || chat.participantNames?.[uid] || '名無し';
            const isMe = uid === currentUser.uid;
            const isOrg = uid === chat.organizerId;
            return (
              <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Link
                  to={`/users/${uid}`}
                  style={{ color: 'var(--alpine)', textDecoration: 'underline', fontSize: '0.85rem' }}
                >
                  {name}
                </Link>
                {isMe && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>あなた</span>}
                {isOrg && <span style={{ fontSize: '0.7rem', color: 'var(--alpine)' }}>主催者</span>}
                {!isMe && !isDissolved && (
                  disclosedTo[uid] ? (
                    <button
                      className="btn btn-sm btn-danger"
                      style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                      onClick={() => handleRevokePrivateInfo(uid)}
                    >
                      公開中止
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-outline"
                      style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                      onClick={() => handleSharePrivateInfo(uid)}
                    >
                      非公開情報を公開する
                    </button>
                  )
                )}
                {isOrganizer && !isMe && (
                  <button
                    className="btn btn-sm btn-danger"
                    style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                    onClick={() => handleRemoveParticipant(uid)}
                    title="承認解除・削除"
                  >
                    削除
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* メッセージエリア */}
      <div className="chat-messages card">
        {messages.length === 0 && (
          <p className="no-messages">まだメッセージがありません。最初のメッセージを送りましょう！</p>
        )}
        {messages.map(msg => {
          const isMe = msg.senderId === currentUser.uid;
          return (
            <div key={msg.id} className={`message ${isMe ? 'mine' : 'theirs'}`}>
              {!isMe && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  {msg.senderName}
                </p>
              )}
              <div className="message-bubble">
                <p className="message-text">{msg.text}</p>
                <div className="message-footer">
                  <p className="message-time">
                    {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleString('ja-JP') : ''}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* 入力エリア */}
      <form onSubmit={handleSend} className="chat-input card">
        <input
          type="text"
          className="form-control"
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder={isDissolved ? '解散済みのためメッセージを送れません' : 'メッセージを入力...'}
          maxLength={500}
          disabled={sending || isDissolved}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={sending || !newMessage.trim() || isDissolved}
        >
          {sending ? '送信中...' : '送信'}
        </button>
      </form>
    </div>
  );
}

export default GroupChat;
